import { useEffect, useRef, useState } from "react";
import AppShell from "../../components/app/AppShell";
import AppEmptyState from "../../components/app/AppEmptyState";
import { FullPageLoading, InlineSpinner } from "../../components/Loading";
import {
  deletePhoto,
  listPhotos,
  listProfiles,
  updatePhotoCaption,
  uploadPhoto,
  type Photo,
} from "../../lib/api";

// /app/photos — upload + grid + per-tile caption + delete.
// Files live in Cloud Storage at photos/{phone}/{uuid}.{ext}; metadata in
// Firestore. Signed URLs are short-lived (60 min) so we refetch the list
// after any mutation rather than caching the URL.

const QUOTA_BASIS = 10; // Basis tier
const ROTATIONS = ["-1.5deg", "0.8deg", "-0.5deg", "1.2deg", "-1deg", "0.4deg"];

export default function AppPhotos() {
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState<string | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function flashToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  }

  async function reload(p: string) {
    try {
      const r = await listPhotos(p);
      setPhotos(r.photos || []);
      setError(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Konnte Fotos nicht laden.");
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0];
        if (!p) {
          setLoading(false);
          return;
        }
        setPhone(p.phone);
        await reload(p.phone);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <FullPageLoading />;

  if (!phone) {
    return (
      <AppShell
        eyebrow="Mein Buch"
        title={
          <>
            Fotos für <em>das Buch</em>
          </>
        }
      >
        <AppEmptyState
          icon="photo"
          title="Noch kein Erzähler-Profil verknüpft."
          body="Foto-Uploads gehören zu einem Erzähler-Profil, das per Telefonnummer identifiziert wird."
        />
      </AppShell>
    );
  }

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    if (!phone) return;
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    setError(null);
    let uploaded = 0;
    try {
      for (const file of files) {
        await uploadPhoto(phone, file);
        uploaded += 1;
      }
      await reload(phone);
      flashToast(
        uploaded === 1
          ? "Foto hinzugefügt."
          : `${uploaded} Fotos hinzugefügt.`,
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function onSaveCaption(p: Photo, next: string) {
    if (!phone || next === p.caption) return;
    setBusyId(p.id);
    try {
      await updatePhotoCaption(phone, p.id, next);
      setPhotos((cur) =>
        cur.map((x) => (x.id === p.id ? { ...x, caption: next } : x)),
      );
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Bildunterschrift fehlgeschlagen.",
      );
    } finally {
      setBusyId(null);
    }
  }

  async function onDelete(p: Photo) {
    if (!phone) return;
    const ok = window.confirm("Foto wirklich entfernen?");
    if (!ok) return;
    setBusyId(p.id);
    try {
      await deletePhoto(phone, p.id);
      setPhotos((cur) => cur.filter((x) => x.id !== p.id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Konnte nicht löschen.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <AppShell
      eyebrow="Mein Buch"
      title={
        <>
          Fotos für <em>das Buch</em>
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        <p
          style={{
            fontFamily: "var(--f-body)",
            fontSize: 15,
            lineHeight: 1.5,
            color: "var(--ink-soft)",
            margin: "0 0 14px",
          }}
        >
          Familien-Schnappschüsse, alte Aufnahmen, Lieblingsorte — wir betten
          sie ins Buch ein.
        </p>

        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(184,52,40,0.08)",
              border: "1px solid rgba(184,52,40,0.30)",
              borderRadius: 8,
              padding: "10px 14px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--error)",
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        {toast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              background: "rgba(95,107,79,0.10)",
              border: "1px solid rgba(95,107,79,0.30)",
              borderLeft: "3px solid var(--sage)",
              borderRadius: 8,
              padding: "10px 14px",
              fontFamily: "var(--f-body)",
              fontSize: 14,
              color: "var(--sage)",
              marginBottom: 14,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {toast}
          </div>
        )}

        {/* Upload zone */}
        <label
          htmlFor="photo-input"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            background: "var(--paper-50)",
            border: "2px dashed rgba(168,122,58,0.50)",
            borderRadius: 14,
            padding: "26px 18px",
            cursor: uploading ? "wait" : "pointer",
            marginBottom: 22,
            opacity: uploading ? 0.7 : 1,
          }}
        >
          {uploading ? (
            <InlineSpinner />
          ) : (
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--ochre)"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <circle cx="9" cy="11" r="2" />
              <path d="M21 15l-5-5L5 19" />
            </svg>
          )}
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontWeight: 500,
              fontSize: 16,
              color: "var(--ink)",
            }}
          >
            {uploading ? "Lädt hoch …" : "Fotos hinzufügen"}
          </div>
          <div
            style={{
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 12.5,
              color: "var(--ink-faint)",
              textAlign: "center",
            }}
          >
            JPG, PNG, WebP oder HEIC · max. 8 MB pro Datei
          </div>
          <input
            id="photo-input"
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic"
            multiple
            disabled={uploading}
            onChange={onPick}
            style={{ display: "none" }}
          />
        </label>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 10,
          }}
        >
          <div className="t-caps">Sammlung</div>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
            }}
          >
            {photos.length} von {QUOTA_BASIS}
          </div>
        </div>

        {photos.length === 0 ? (
          <div
            style={{
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--ink-faint)",
              padding: "24px 0",
              textAlign: "center",
            }}
          >
            Noch keine Fotos. Beginnen Sie mit einem Lieblingsbild.
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 14,
            }}
          >
            {photos.map((p, i) => (
              <Polaroid
                key={p.id}
                photo={p}
                rotation={ROTATIONS[i % ROTATIONS.length]}
                busy={busyId === p.id}
                onSaveCaption={(c) => onSaveCaption(p, c)}
                onDelete={() => onDelete(p)}
              />
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Polaroid({
  photo,
  rotation,
  busy,
  onSaveCaption,
  onDelete,
}: {
  photo: Photo;
  rotation: string;
  busy: boolean;
  onSaveCaption: (caption: string) => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [caption, setCaption] = useState(photo.caption);

  useEffect(() => {
    setCaption(photo.caption);
  }, [photo.caption]);

  function commit() {
    setEditing(false);
    onSaveCaption(caption.trim());
  }

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(43,26,15,0.08)",
        boxShadow: "0 6px 14px -8px rgba(43,26,15,0.30)",
        padding: "8px 8px 14px",
        transform: `rotate(${rotation})`,
        position: "relative",
      }}
    >
      <div
        style={{
          width: "100%",
          aspectRatio: "1 / 1",
          background: "var(--paper-200)",
          overflow: "hidden",
          marginBottom: 8,
        }}
      >
        {photo.download_url ? (
          <img
            src={photo.download_url}
            alt={photo.caption || "Foto"}
            className="photo-tone"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 11,
              color: "var(--ink-faint)",
            }}
          >
            (kein Vorschau-Link)
          </div>
        )}
      </div>

      {editing ? (
        <input
          type="text"
          autoFocus
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setCaption(photo.caption);
              setEditing(false);
            }
          }}
          placeholder="Bildunterschrift"
          style={{
            width: "100%",
            background: "transparent",
            border: "none",
            borderBottom: "1px dashed rgba(43,26,15,0.30)",
            outline: "none",
            padding: "2px 0 3px",
            fontFamily: "var(--f-script)",
            fontSize: 17,
            color: "var(--oxblood)",
            textAlign: "center",
            boxSizing: "border-box",
          }}
        />
      ) : (
        <div
          onClick={() => !busy && setEditing(true)}
          style={{
            fontFamily: "var(--f-script)",
            fontSize: 17,
            color: photo.caption ? "var(--oxblood)" : "var(--ink-faint)",
            textAlign: "center",
            minHeight: 22,
            cursor: busy ? "wait" : "text",
            lineHeight: 1.2,
          }}
        >
          {photo.caption || "+ Bildunterschrift"}
        </div>
      )}

      <button
        type="button"
        onClick={onDelete}
        disabled={busy}
        title="Foto entfernen"
        style={{
          position: "absolute",
          top: 4,
          right: 4,
          width: 24,
          height: 24,
          borderRadius: "50%",
          border: "none",
          background: "rgba(43,26,15,0.55)",
          color: "#fff",
          fontFamily: "var(--f-ui)",
          fontSize: 14,
          lineHeight: 1,
          cursor: busy ? "wait" : "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {busy ? "…" : "×"}
      </button>
    </div>
  );
}
